<template>
    <div class="panel panel-default">
        <div class="panel-heading">
            <div class="row">
                <div class="col-xs-18">
                    <h5>
                        Company Subscription Breakdown
                    </h5>
                </div>

                <div class="col-xs-3" style="padding-top: 3px">
                    <select-widget
                            v-model="breakdown"
                            :options="[{value: 'portals', label: 'Portals'}, {value: 'users', label: 'Users'}]"
                            class="input-sm"
                    ></select-widget>
                </div>

                <div class="col-xs-3" style="padding-top: 3px">
                    <select-widget
                            v-model="timeUnit"
                            :options="[{value: 'years', label: 'Years'}, {value: 'months', label: 'Months'}, {value: 'weeks', label: 'Weeks'}]"
                            class="input-sm"
                    ></select-widget>
                </div>
            </div>
        </div>

        <loading-spinner v-if="loading" :style="{margin: '30px'}"></loading-spinner>

        <div v-else class="panel-body">
            <div v-if="isEmpty" class="text-center" style="padding: 50px">
                <i>
                    There are no subscription periods to display. Try adjusting the filters.
                </i>
            </div>

            <div v-else>
                <canvas width="800" height="250"></canvas>

                <table class="table table-condensed table-striped">
                    <thead>
                    <tr>
                        <th class="col-xs-1"></th>

                        <th class="col-xs-3">
                            Sales Date
                        </th>

                        <th class="col-xs-6">
                            Period
                        </th>

                        <th class="col-xs-5">
                            Customer
                        </th>

                        <th class="col-xs-3">
                            User
                        </th>

                        <th class="col-xs-3">
                            Portal
                        </th>

                        <th class="col-xs-2">
                            Price
                        </th>

                        <th class="col-xs-1"></th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr v-for="row in pagedDetailList">
                        <td class="text-center">
                            <input type="checkbox" :disabled="true">
                        </td>

                        <td>
                            {{ row.sales_date | date }}
                        </td>

                        <td>
                            <router-link
                                    :to="'/' + row.subscriber_type + '/' + row.subscriber_id + '/subscriptions/' + row.subscription_id"
                            >
                                {{ row.subscription_period_start_date | date }} - {{ row.subscription_period_end_date |
                                date }}
                            </router-link>
                        </td>

                        <td class="ellipsis">
                            <router-link :to="'/' + row.subscriber_type + '/' + row.subscriber_id">
                                {{ row.subscriber_name }}
                            </router-link>
                        </td>

                        <td class="ellipsis">
                            <router-link v-if="row.user_id" :to="'/users/' + row.user_id">
                                {{ row.user_initials }}
                            </router-link>
                        </td>

                        <td class="ellipsis">
                            <router-link :to="'/portals/' + row.portal_id">
                                {{ row.portal_name }}
                            </router-link>
                        </td>

                        <td>
                            {{ row.subscription_revenue | price }}
                        </td>

                        <td class="text-right">
                            <div class="btn-group list-actions">
                                <router-link
                                        :to="'/' + row.subscriber_type + '/' + row.subscriber_id + '/subscriptions/' + row.subscription_id"
                                        class="btn btn-sm"
                                >
                                    <i class="fa fa-search"></i>
                                </router-link>

                                <button type="button" class="btn btn-sm" :disabled="true">
                                    <i class="fa fa-trash"></i>
                                </button>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>

                <simple-pager :current-page="page" :last-page="lastPage" @navigate="page = $event"></simple-pager>
            </div>
        </div>

        <subscription-breakdown-modal
                v-if="from && to"
                :from="from"
                :to="to"
                :users="baseUserIds"
                :portals="basePortalIds"
                :company="baseCompanyId"
        />
    </div>
</template>

<script>
    import datalabels from 'chartjs-plugin-datalabels'
    import http from '@/http'
    import moment from 'moment'
    import LoadingSpinner from '@/components/Layout/LoadingSpinner'
    import Pager from '@/components/Lists/Pager'
    import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
    import SimplePager from "../../../components/Lists/SimplePager";
    import SubscriptionBreakdownModal from '@/components/Modals/Dashboard/Sales/SubscriptionBreakdownModal'

    const totalizer = {
        id: 'totalizer',
        beforeUpdate: function (chart) {
            let totals = [];

            let utmost = {};

            chart.data.datasets.forEach(function (dataset, datasetIndex) {
                if (chart.isDatasetVisible(datasetIndex)) {
                    let stack = dataset.stack;

                    utmost[stack] = datasetIndex;

                    dataset.data.forEach(function (value, index) {
                        if (totals[index] === undefined) {
                            totals[index] = {};
                        }

                        totals[index][stack] = (totals[index][stack] || 0) + value;
                    })
                }
            });

            chart.$totalizer = {
                totals: totals,
                utmost: utmost
            }
        }
    }

    export default {
        components: {SimplePager, LoadingSpinner, Pager, SelectWidget, SubscriptionBreakdownModal},

        props: {
            compare: {required: true},
            filters: {required: true},
            compareFilters: {required: true}
        },

        data() {
            return {
                baseRows: null,
                compareRows: null,

                detailList: null,
                page: 1,

                breakdown: 'portals',
                timeUnit: 'months',

                loading: true,

                from: null,
                to: null,

                chart: null
            }
        },

        watch: {
            filters() {
                this.load()
            },

            compare() {
                this.load()
            },

            compareFilters() {
                this.load()
            },

            breakdown() {
                this.load()
            },

            timeUnit() {
                this.load()
            },

            loading(value) {
                if (!value && !this.isEmpty) {
                    this.$nextTick(this.draw)
                }
            }
        },

        mounted() {
            this.load()
        },

        computed: {
            isEmpty() {
                if (this.compare) {
                    return this.baseRows.length === 0 && this.compareRows.length === 0
                }

                return this.baseRows.length === 0
            },

            breakdownItems() {
                return Object.values(this.baseBreakdownItems.concat(this.compareBreakdownItems).reduce((result, row) => {
                    result[row.id] = row

                    return result
                }, {}))
            },

            baseBreakdownItems() {
                return Object.values(this.baseRows.reduce((result, row) => {
                    result[row[this.breakdownIdKey]] = {
                        id: row[this.breakdownIdKey],
                        name: row[this.breakdownNameKey],
                        color: row[this.breakdownColorKey]
                    }

                    return result
                }, {}))
            },

            compareBreakdownItems() {
                if (!this.compare) {
                    return []
                }

                return Object.values(this.compareRows.reduce((result, row) => {
                    result[row[this.breakdownIdKey]] = {
                        id: row[this.breakdownIdKey],
                        name: row[this.breakdownNameKey],
                        color: row[this.breakdownColorKey]
                    }

                    return result
                }, {}))
            },

            baseSoldFrom() {
                return this.filters.find(filter => filter.dimension === 'sales_date' && filter.operator === '>=').value
            },

            baseSoldTo() {
                return this.filters.find(filter => filter.dimension === 'sales_date' && filter.operator === '<=').value
            },

            baseUserIds() {
                let filter = this.filters.find(filter => filter.dimension === 'user_id')

                return filter ? filter.value : null
            },

            basePortalIds() {
                let filter = this.filters.find(filter => filter.dimension === 'portal_id')

                return filter ? filter.value : null
            },

            baseCompanyId() {
                let filter = this.filters.find(filter => filter.dimension === 'subscriber_id')

                return filter ? filter.value : null
            },

            baseTimeItems() {
                return this.baseTimes.map(baseTime => baseTime.format(this.timeFormat))
            },

            baseTimes() {
                let actual = moment(this.baseSoldFrom).startOf(this.startOfTimeUnit)

                let timeUnit = []

                while (actual <= moment(this.baseSoldTo)) {
                    timeUnit.push(moment(actual))

                    actual.add(1, this.timeUnit)
                }

                return timeUnit
            },

            compareSoldFrom() {
                return this.compareFilters.find(filter => filter.dimension === 'sales_date' && filter.operator === '>=').value
            },

            compareSoldTo() {
                return this.compareFilters.find(filter => filter.dimension === 'sales_date' && filter.operator === '<=').value
            },

            compareTimeItems() {
                return this.compareTimes.map(compareTime => compareTime.format(this.timeFormat))
            },

            compareTimes() {
                let actual = moment(this.compareSoldFrom).startOf(this.startOfTimeUnit)

                let timeUnit = []

                while (actual <= moment(this.compareSoldTo)) {
                    timeUnit.push(moment(actual))

                    actual.add(1, this.timeUnit)
                }

                return timeUnit
            },

            datasets() {
                return this.baseDatasets.concat(this.compareDatasets)
            },

            baseDatasets() {
                return this.breakdownItems.map(breakdownItem => {
                    return {
                        label: breakdownItem.name,
                        data: this.getBaseDataset(breakdownItem),
                        backgroundColor: breakdownItem.color,
                        stack: 1
                    }
                })
            },

            compareDatasets() {
                if (!this.compare) {
                    return []
                }

                return this.breakdownItems.map(breakdownItem => {
                    return {
                        label: breakdownItem.name,
                        data: this.getCompareDataset(breakdownItem),
                        backgroundColor: breakdownItem.color,
                        stack: 2
                    }
                })
            },

            labels() {
                let labels = []

                for (let i = 0; i < this.baseTimeItems.length; i++) {
                    let label = this.baseTimeItems[i]

                    if (this.compare && typeof this.compareTimeItems[i] !== 'undefined') {
                        label = label + ' (' + this.compareTimeItems[i] + ')'
                    }

                    labels.push(label)
                }

                return labels
            },

            breakdownIdKey() {
                if (this.breakdown === 'portals') {
                    return 'portal_id'
                }

                return 'user_id'
            },

            breakdownNameKey() {
                if (this.breakdown === 'portals') {
                    return 'portal_name'
                }

                return 'user_initials'
            },

            breakdownColorKey() {
                if (this.breakdown === 'portals') {
                    return 'portal_color'
                }

                return 'user_color'
            },

            timeFormat() {
                if (this.timeUnit === 'years') {
                    return 'YYYY'
                }

                if (this.timeUnit === 'months') {
                    return 'YYYY-MM'
                }

                return 'GGGG-WW'
            },

            timeUnitKey() {
                if (this.timeUnit === 'years') {
                    return 'sales_year'
                }

                if (this.timeUnit === 'months') {
                    return 'sales_month'
                }

                return 'sales_week'
            },

            startOfTimeUnit() {
                if (this.timeUnit === 'years') {
                    return 'year'
                }

                if (this.timeUnit === 'months') {
                    return 'month'
                }

                return 'isoWeek'
            },

            pagedDetailList() {
                return this.detailList.slice((this.page - 1) * 10, this.page * 10)
            },

            lastPage() {
                return Math.ceil(this.detailList.length / 10)
            }
        },

        methods: {
            load() {
                this.page = 1

                this.loading = true

                Promise.all(this.getPromises())
                    .then(responses => {
                        this.baseRows = responses[0].data.data

                        this.detailList = responses[1].data.data.map(row => {
                            row.subscription_revenue = (row.company_subscription_revenue ? row.company_subscription_revenue : 0) + (row.person_subscription_revenue ? row.person_subscription_revenue : 0)

                            return row
                        })

                        this.detailList.sort((a, b) => moment(a.sales_date).isBefore(b.sales_date) ? 1 : -1)

                        if (this.compare) {
                            this.compareRows = responses[2].data.data
                        }

                        this.loading = false
                    })
            },

            getPromises() {
                let promises = [
                    http.post('/api/v1/reports/leadout-dashboard', {
                        metrics: ['subscription_revenue'],
                        filters: this.filters,
                        dimensions: [this.timeUnitKey, this.breakdownIdKey, this.breakdownNameKey, this.breakdownColorKey]
                    }),
                    http.post('/api/v1/reports/leadout-dashboard', {
                        metrics: ['company_subscription_revenue', 'person_subscription_revenue'],
                        filters: this.filters,
                        dimensions: [
                            'sales_date',
                            'subscription_period_start_date',
                            'subscription_period_end_date',
                            'subscriber_id',
                            'subscriber_type',
                            'subscription_id',
                            'subscriber_name',
                            'user_initials',
                            'user_id', 'portal_name', 'portal_id'
                        ]
                    }),
                ]

                if (this.compare) {
                    promises.push(
                        http.post('/api/v1/reports/leadout-dashboard', {
                            metrics: ['subscription_revenue'],
                            filters: this.compareFilters,
                            dimensions: [this.timeUnitKey, this.breakdownIdKey, this.breakdownNameKey, this.breakdownColorKey]
                        })
                    )
                }

                return promises
            },

            draw() {
                this.chart = new Chart(this.$el.querySelector('canvas').getContext('2d'), {
                    plugins: [datalabels, totalizer],

                    type: 'bar',

                    data: {
                        labels: this.labels,
                        datasets: this.datasets
                    },

                    options: {
                        scales: {
                            xAxes: [{
                                stacked: true
                            }],

                            yAxes: [{
                                stacked: true,
                                ticks: {
                                    beginAtZero: true,
                                    callback: value => this.$options.filters.price(value)
                                }
                            }]
                        },

                        legend: {
                            position: 'bottom',
                            onClick: () => {
                            },
                            labels: {
                                generateLabels: () => {
                                    return this.breakdownItems.map(breakdownItem => {
                                        return {
                                            text: breakdownItem.name,
                                            fillStyle: breakdownItem.color
                                        }
                                    })
                                }
                            }
                        },

                        layout: {
                            padding: {
                                top: 25
                            }
                        },

                        plugins: {
                            datalabels: {
                                anchor: 'end',
                                align: 'end',
                                formatter: (value, ctx) => this.$options.filters.price(ctx.chart.$totalizer.totals[ctx.dataIndex][ctx.dataset.stack]),
                                display: ctx => ctx.datasetIndex === ctx.chart.$totalizer.utmost[ctx.dataset.stack]
                            }
                        },

                        tooltips: {
                            callbacks: {
                                label: (t, d) => d.datasets[t.datasetIndex].label + ': ' + this.$options.filters.price(d.datasets[t.datasetIndex].data[t.index]),
                                title: (t, d) => d.datasets[t[0].datasetIndex].stack === 1 ? this.baseTimeItems[t[0].index] : this.compareTimeItems[t[0].index]
                            }
                        },

                        onClick: event => {
                            if (this.chart.getElementAtEvent(event).length) {
                                let element = this.chart.getElementAtEvent(event)[0]

                                if (this.datasets[element._datasetIndex].stack === 1) {
                                    this.from = moment(this.baseTimes[element._index]).startOf(this.startOfTimeUnit).format('YYYY-MM-DD')

                                    this.to = moment(this.baseTimes[element._index]).endOf(this.startOfTimeUnit).format('YYYY-MM-DD')
                                }

                                if (this.datasets[element._datasetIndex].stack === 2) {
                                    this.from = moment(this.compareTimes[element._index]).startOf(this.startOfTimeUnit).format('YYYY-MM-DD')

                                    this.to = moment(this.compareTimes[element._index]).endOf(this.startOfTimeUnit).format('YYYY-MM-DD')
                                }

                                this.$nextTick(() => $('#subscription-sales-breakdown-modal').modal('show'))
                            }
                        }
                    }
                });
            },

            getBaseDataset(breakdownItem) {
                return this.baseTimeItems.map(timeItem => {
                    return this.getBaseValue(breakdownItem, timeItem)
                })
            },

            getBaseValue(breakdownItem, timeItem) {
                let row = this.baseRows.find(row => row[this.breakdownIdKey] === breakdownItem.id && row[this.timeUnitKey] === timeItem)

                return row ? row.subscription_revenue : 0
            },

            getCompareDataset(breakdownItem) {
                return this.compareTimeItems.map(timeItem => {
                    return this.getCompareValue(breakdownItem, timeItem)
                })
            },

            getCompareValue(breakdownItem, timeItem) {
                let row = this.compareRows.find(row => row[this.breakdownIdKey] === breakdownItem.id && row[this.timeUnitKey] === timeItem)

                return row ? row.subscription_revenue : 0
            }
        }
    }
</script>
